/**
 * `Shift+F10` і клавіша меню — меню елемента у фокусі (keyboard.md, розд. 1; LST-23, KAN-11).
 *
 * - **Подія `contextmenu`**, а не `keydown`: браузер сам дає її з обох клавіш, а скасування `keydown`
 *   у Windows нативного меню не прибирає. Діємо лише на подію з клавіатури — перед нею не було
 *   `pointerdown`; правий клік мишею лишає меню браузера (правий клік у LST-23 — Next).
 * - **macOS:** там `Shift+F10` події `contextmenu` не дає, а клавіші меню немає — запасний `keydown`.
 *   Браузер, що там таки дає `contextmenu` слідом (рев'ю QA C2), щойно відкрите меню не закриває:
 *   першу `contextmenu` після запасного `keydown` пропускаємо.
 */
import { type KeyboardEvent, type MouseEvent, useRef } from 'react';
import { isMac } from './hotkeys.ts';

export interface MenuKeyHandlers {
  onPointerDown: () => void;
  /** `true` — подію використано (запасний `Shift+F10` на macOS), далі не обробляти. */
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => boolean;
  onContextMenu: (e: MouseEvent<HTMLElement>) => void;
}

/** `open(target)` відкриває меню елемента події і каже, чи відкрило (`false` — меню браузера). */
export function useMenuKey(open: (target: HTMLElement) => boolean): MenuKeyHandlers {
  const pointer = useRef(false);
  const handled = useRef(false);
  const run = useRef(open);
  run.current = open;
  const handlers = useRef<MenuKeyHandlers | null>(null);
  handlers.current ??= {
    onPointerDown: () => {
      pointer.current = true;
      handled.current = false;
    },
    onKeyDown: (e) => {
      pointer.current = false;
      handled.current = false;
      const menuKey = e.key === 'F10' && e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey;
      if (!menuKey || !isMac() || e.nativeEvent.isComposing) return false;
      if (!run.current(e.target as HTMLElement)) return false;
      e.preventDefault();
      handled.current = true;
      return true;
    },
    onContextMenu: (e) => {
      if (pointer.current) {
        pointer.current = false;
        return;
      }
      if (handled.current) {
        handled.current = false;
        e.preventDefault();
        return;
      }
      if (run.current(e.target as HTMLElement)) e.preventDefault();
    },
  };
  return handlers.current;
}
